/**
 * What to do with the stored Supabase session when the app boots — which is
 * every time a new build is applied, because applying an update is a reload.
 *
 * Users were being signed out by updates. Two causes, both at boot:
 *
 *  1. Plain browser / PWA. The update reload usually lands while the tab is
 *     backgrounded (phones apply it the moment the app is hidden) or after a
 *     long idle, so the access token has expired and getSession() has to
 *     refresh it. On a suspended or flaky connection that refresh fails with
 *     a RETRYABLE error and getSession() returns `session: null` alongside
 *     it. Boot treated any null as "signed out" and called logout(), which
 *     deletes the still-good refresh token. A timeout went to the login page.
 *
 *  2. Native shells (iOS / macOS). The shell owns the refresh-token family and
 *     the web copy never refreshes, so after an hour the web copy is expired.
 *     getSession() on an expired copy refreshes it anyway — with a refresh
 *     token the shell has usually rotated already. Supabase treats that as
 *     reuse, revokes the whole family, the web calls logout(), and logout()
 *     tells the shell to sign out too.
 *
 * The rule now: only a definitive rejection of the refresh token signs the
 * user out. Anything transient keeps them in, and inside a shell an expired
 * web copy is never refreshed at boot — the shell is asked for its session.
 */

/** Same key the singleton client is created with (utils/supabase/client.ts). */
export const SUPABASE_AUTH_STORAGE_KEY = 'contract-group-auth';

/** auth-js refreshes a session this close to expiry inside getSession()
 *  (EXPIRY_MARGIN_MS = 3 ticks x 30s). Matching it means "live" here is
 *  exactly "getSession() will not touch the network". */
export const SUPABASE_EXPIRY_MARGIN_MS = 90_000;

export interface StoredSessionPeek {
  accessToken: string;
  expiresAt: number | null;
  hasRefreshToken: boolean;
  userId: string | null;
}

/** Read the persisted session WITHOUT going through auth-js, so nothing is
 *  refreshed as a side effect. Returns null when absent or unreadable. */
export function peekStoredSession(
  storage: Pick<Storage, 'getItem'> | undefined = typeof window !== 'undefined' ? window.localStorage : undefined,
  key: string = SUPABASE_AUTH_STORAGE_KEY,
): StoredSessionPeek | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { access_token?: unknown; refresh_token?: unknown; expires_at?: unknown; user?: { id?: unknown } };
    if (!parsed || typeof parsed.access_token !== 'string') return null;
    return {
      accessToken: parsed.access_token,
      expiresAt: typeof parsed.expires_at === 'number' ? parsed.expires_at : null,
      hasRefreshToken: typeof parsed.refresh_token === 'string' && parsed.refresh_token.length > 0,
      userId: typeof parsed.user?.id === 'string' ? parsed.user.id : null,
    };
  } catch {
    return null;
  }
}

/** True when getSession() would hand this session back without refreshing. */
export function isStoredSessionLive(peek: StoredSessionPeek | null, nowMs: number): boolean {
  if (!peek) return false;
  if (peek.expiresAt === null) return true;
  return peek.expiresAt * 1000 - nowMs > SUPABASE_EXPIRY_MARGIN_MS;
}

/** Whether boot can draw the app on the saved user before the session check
 *  answers. The check still runs and signs out a dead session; this only
 *  stops the launch screen waiting on it. A shell must not refresh the web
 *  copy, so there only an unexpired session counts. */
export function canDrawSavedSession(input: {
  peek: StoredSessionPeek | null;
  savedUserId: string | null | undefined;
  native: boolean;
  nowMs: number;
}): boolean {
  if (!input.peek || !input.savedUserId || input.peek.userId !== input.savedUserId) return false;
  return input.native ? isStoredSessionLive(input.peek, input.nowMs) : input.peek.hasRefreshToken;
}

export type BootSessionVerdict =
  /** A usable session came back. */
  | 'live'
  /** Nothing stored at all — the saved app user is a leftover. */
  | 'absent'
  /** The auth server rejected the refresh token. Genuinely signed out. */
  | 'dead'
  /** Network, timeout or 5xx. Says nothing about the session: keep it. */
  | 'transient';

export function classifyBootSession(input: {
  session: unknown;
  error: unknown;
  timedOut: boolean;
  isDeadSessionError: (error: unknown) => boolean;
}): BootSessionVerdict {
  if (input.timedOut) return 'transient';
  if (input.session) return 'live';
  if (!input.error) return 'absent';
  return input.isDeadSessionError(input.error) ? 'dead' : 'transient';
}

/** Back-off for re-checking a session kept through a transient failure. */
export const TRANSIENT_RECHECK_DELAYS_MS = [5_000, 15_000, 30_000, 60_000] as const;

/** How long a native shell gets to answer auth:requestSession at boot before
 *  the login screen shows (nothing is cleared — a late answer still signs in). */
export const NATIVE_SESSION_BOOT_WAIT_MS = 12_000;
